/**
 * Sensitive field patterns that should be redacted from logs
 *
 * SECURITY: These fields may contain PII, credentials, or sensitive data
 *
 * Kept in its own module, rather than inside `impl/pino.ts`, so callers can
 * check a key against the list without importing the pino implementation —
 * that module builds a logger at evaluation time, and `index.ts` loads it
 * lazily on purpose. A call site that logs a value it needs to READ BACK can
 * assert its key survives redaction (see the Connect service's tests).
 */
export const SENSITIVE_FIELDS = [
  // Authentication & Tokens
  'password',
  'token',
  'accessToken',
  'access_token',
  'refreshToken',
  'refresh_token',
  'apiKey',
  'api_key',
  'secret',
  'apiSecret',
  'api_secret',
  'jwt',
  'bearer',
  'authorization',

  // Personal Identifiable Information (PII)
  'email',
  'phone',
  'phone_number',
  'ssn',
  'social_security',
  'credit_card',
  'creditCard',
  'card_number',

  // Sensitive User Data
  'user_id',
  'userId',
  'account_id',
  'accountId',

  // Database Credentials
  'db_password',
  'database_password',
  'connectionString',
  'connection_string',

  // OAuth & External Services
  'client_secret',
  'clientSecret',
  'private_key',
  'privateKey',
];

/**
 * Redaction paths covering both top-level keys and nested occurrences.
 *
 * SECURITY: pino's `redact.paths` with a bare field name only matches the
 * top-level key of the logged object. Sensitive values frequently appear
 * nested (e.g. `user.email`, an `error` payload carrying a `token`), so each
 * sensitive field is also registered under single-level (`*.field`) and
 * two-level (`*.*.field`) wildcards to redact those nested occurrences.
 */
export const REDACT_PATHS = SENSITIVE_FIELDS.flatMap((field) => [
  field,
  `*.${field}`,
  `*.*.${field}`,
]);
